import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, HeartIcon, ClockIcon, TargetIcon, ZapIcon, XIcon } from 'lucide-react';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-31">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-32">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-33" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="relative bg-[#0a0e27] border border-[#d4a853]/30 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-34">
            <div className="sticky top-0 bg-[#0a0e27] border-b border-[#d4a853]/20 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-35">
              <h2 className="font-playfair text-xl text-[#d4a853]" data-id="element-36">{title}</h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f0e8]/10 hover:bg-[#f5f0e8]/20 transition-colors" data-id="element-37">
                <XIcon className="w-4 h-4 text-[#f5f0e8]" data-id="element-38" />
              </button>
            </div>
            <div className="px-6 py-6 font-dm-sans text-[#f5f0e8]/80 text-sm leading-relaxed space-y-4" data-id="element-39">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function SalesLetterClosing() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const results = [{
    icon: HeartIcon,
    text: 'Vas a empezar a respetarte más.'
  }, {
    icon: ZapIcon,
    text: 'A decidir con más calma.'
  }, {
    icon: ShieldCheckIcon,
    text: 'A dejar de negociar tu valor.'
  }, {
    icon: ClockIcon,
    text: 'A sostener hábitos simples.'
  }, {
    icon: TargetIcon,
    text: 'A sentir dirección.'
  }];
  return <section ref={ref} className="w-full bg-gradient-to-b from-[#0a0e27] via-[#1a1a4e] to-[#0a0e27] py-20 px-4 md:py-28" data-id="element-40">
      <div className="max-w-4xl mx-auto" data-id="element-41">
        {/* Important Note */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-42">
          <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#f5f0e8] mb-4" data-id="element-43">
            Lo más importante: este libro{' '}
            <span className="text-red-400" data-id="element-44">NO</span> funciona por entender
          </h2>
          <p className="font-playfair text-2xl md:text-3xl text-[#d4a853]" data-id="element-45">
            Funciona por practicar.
          </p>
        </motion.div>

        {/* Results */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mb-16" data-id="element-46">
          <p className="font-dm-sans text-lg text-[#f5f0e8]/80 text-center mb-8" data-id="element-47">
            Y si lo practicas, va a pasar algo muy específico:
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-id="element-48">
            {results.map((result, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={isInView ? {
            opacity: 1,
            scale: 1
          } : {}} transition={{
            duration: 0.4,
            delay: 0.3 + index * 0.1
          }} className="flex items-center gap-3 bg-[#1a1a4e]/50 rounded-lg p-4" data-id="element-49">
                <result.icon className="w-5 h-5 text-[#d4a853] flex-shrink-0" data-id="element-50" />
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-51">{result.text}</p>
              </motion.div>)}
          </div>

          <p className="font-playfair text-xl md:text-2xl text-[#f0c75e] text-center" data-id="element-52">
            Eso es transformación real.
          </p>
        </motion.div>

        {/* CTA Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mb-16" data-id="element-53">
          <div className="bg-gradient-to-br from-[#d4a853]/20 to-[#d4a853]/10 border-2 border-[#d4a853]/50 rounded-2xl p-8 md:p-12 text-center" data-id="element-54">
            <h3 className="font-playfair text-xl md:text-2xl text-[#f5f0e8] mb-2" data-id="element-55">
              Tu siguiente paso:
            </h3>
            <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#d4a853] mb-6" data-id="element-56">
              Reto "7 Días de Intuición y Leyes Universales"
            </h2>

            <p className="font-dm-sans text-lg text-[#f5f0e8]/80 mb-6" data-id="element-57">
              Si quieres que esto no se quede en lectura, sino en resultados, al
              final del eBook te invito al reto:
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8" data-id="element-58">
              <div className="bg-[#0a0e27]/50 rounded-lg px-4 py-2" data-id="element-59">
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-60">
                  ⏱️ 10–15 minutos al día
                </p>
              </div>
              <div className="bg-[#0a0e27]/50 rounded-lg px-4 py-2" data-id="element-61">
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-62">
                  🎯 Mentalidad, creencias, estado emocional y acción práctica
                </p>
              </div>
              <div className="bg-[#0a0e27]/50 rounded-lg px-4 py-2" data-id="element-63">
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-64">
                  ✅ Cierre: una decisión concreta + plan de 30 días
                </p>
              </div>
            </div>

            <motion.a href="#cta" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center gap-3 bg-gradient-to-r from-[#d4a853] to-[#f0c75e] text-[#0a0e27] font-dm-sans font-bold text-lg md:text-xl px-10 py-5 rounded-full shadow-lg shadow-[#d4a853]/40 hover:shadow-[#d4a853]/60 transition-shadow" data-id="element-65">
              Quiero empezar el reto
              <ArrowRightIcon className="w-5 h-5" data-id="element-66" />
            </motion.a>

            <p className="font-dm-sans text-sm text-[#f5f0e8]/50 mt-4" data-id="element-67">
              (Premium o gratuito autoguiado)
            </p>
          </div>
        </motion.div>

        {/* Guarantee */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="mb-16" data-id="element-68">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-69">
            <ShieldCheckIcon className="w-7 h-7 text-[#d4a853]" data-id="element-70" />
            <h3 className="font-playfair text-xl md:text-2xl text-[#d4a853]" data-id="element-71">
              Garantía honesta (la que sí es real)
            </h3>
          </div>

          <div className="bg-[#0a0e27]/50 rounded-xl p-6 md:p-8 text-center" data-id="element-72">
            <p className="font-dm-sans text-[#f5f0e8]/80 mb-4" data-id="element-73">
              Este libro no te promete resultados idénticos para todos. Porque
              los resultados dependen de tu aplicación.
            </p>
            <p className="font-dm-sans text-lg text-[#f5f0e8]" data-id="element-74">
              Pero sí te promete algo que es mucho más valioso:
            </p>
            <p className="font-playfair text-xl md:text-2xl text-[#f0c75e] mt-4 italic" data-id="element-75">
              "Un método claro para dejar de vivir en automático… y volver a
              elegir tu vida con intención."
            </p>
          </div>
        </motion.div>

        {/* Final Closing */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center" data-id="element-76">
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#d4a853] to-transparent mx-auto mb-10" data-id="element-77" />

          <p className="font-dm-sans text-lg text-[#f5f0e8]/80 mb-6" data-id="element-78">
            Hay un momento —pequeño, casi invisible— donde decides:
          </p>

          <p className="font-playfair text-xl md:text-2xl text-[#f5f0e8] mb-8 italic" data-id="element-79">
            "O sigo repitiendo… o me vuelvo responsable de mi transformación."
          </p>

          <p className="font-dm-sans text-lg text-[#d4a853] mb-10" data-id="element-80">
            Este eBook existe para ese momento.
          </p>

          <div className="bg-gradient-to-r from-transparent via-[#d4a853]/20 to-transparent py-8 px-4 rounded-xl mb-10" data-id="element-81">
            <p className="font-playfair text-xl md:text-2xl lg:text-3xl text-[#f5f0e8] leading-relaxed" data-id="element-82">
              <span className="text-[#d4a853]" data-id="element-83">
                Activa tu intuición y transforma tu vida con las 7 Leyes
                Universales
              </span>{' '}
              y conviértete en la persona que no solo entiende…{' '}
              <span className="text-[#f0c75e]" data-id="element-84">sino que cambia su vida.</span>
            </p>
          </div>

          {/* Devices Mockup */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mb-10" data-id="element-85"></motion.div>

          {/* Price + Final CTA */}
          <div id="cta" className="scroll-mt-8" data-id="element-86">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-87">
              <span className="font-dm-sans text-lg text-[#f5f0e8]/50 line-through" data-id="element-88">
                $47
              </span>
              <span className="font-playfair text-4xl md:text-5xl font-bold text-[#f0c75e]" data-id="element-89">
                $17
              </span>
              <span className="font-dm-sans text-sm bg-red-500/80 text-white px-3 py-1 rounded-full" data-id="element-90">
                -64% descuento
              </span>
            </div>
            <p className="font-dm-sans text-sm text-[#f5f0e8]/60 mb-6" data-id="element-91">
              Precio especial por tiempo limitado
            </p>

            <motion.a href="https://tusabiduriainfinita.com/cart/47860758577393:1" onClick={() => {
            if (typeof window !== 'undefined' && (window as any).fbq) {
              ;
              (window as any).fbq('track', 'AddToCart');
            }
          }} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center gap-3 bg-gradient-to-r from-[#d4a853] to-[#f0c75e] text-[#0a0e27] font-dm-sans font-bold text-lg md:text-xl px-10 py-5 rounded-full shadow-lg shadow-[#d4a853]/40 hover:shadow-[#d4a853]/60 transition-shadow" data-id="element-92">
              Quiero transformar mi vida ahora
              <ArrowRightIcon className="w-5 h-5" data-id="element-93" />
            </motion.a>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.footer initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-20 pt-10 border-t border-[#d4a853]/20 text-center" data-id="element-94">
          <p className="font-playfair text-lg md:text-xl text-[#d4a853] mb-3" data-id="element-95">
            Activa tu intuición y transforma tu vida con las 7 Leyes Universales
          </p>
          <p className="font-dm-sans text-sm text-[#f5f0e8]/50 mb-4" data-id="element-96">
            © 2026 Gustavo Rodríguez. USA. Todos los derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-97">
            <button onClick={() => setShowPrivacy(true)} className="font-dm-sans text-sm text-[#f5f0e8]/40 hover:text-[#d4a853] transition-colors underline underline-offset-2" data-id="element-98">
              Privacy Policy
            </button>
            <span className="text-[#f5f0e8]/20" data-id="element-99">|</span>
            <button onClick={() => setShowTerms(true)} className="font-dm-sans text-sm text-[#f5f0e8]/40 hover:text-[#d4a853] transition-colors underline underline-offset-2" data-id="element-100">
              Terms &amp; Conditions
            </button>
          </div>
        </motion.footer>
      </div>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-101">
        <p className="text-[#d4a853] font-semibold" data-id="element-102">
          Última actualización: Marzo 2026
        </p>
        <p data-id="element-103">
          Gustavo Rodríguez ("nosotros", "nuestro") opera este sitio web. Esta
          política describe cómo recopilamos, usamos y protegemos tu información
          personal.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-104">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-105">
          Podemos recopilar información personal que nos proporcionas
          voluntariamente, incluyendo: nombre, dirección de correo electrónico y
          datos de pago al realizar una compra.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-106">
          2. Uso de la Información
        </h3>
        <p data-id="element-107">
          Utilizamos tu información para: procesar transacciones y enviar
          confirmaciones de compra, enviar el eBook y materiales bonus
          adquiridos, comunicar actualizaciones relacionadas con tu compra, y
          mejorar nuestros productos y servicios.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-108">
          3. Protección de Datos
        </h3>
        <p data-id="element-109">
          Implementamos medidas de seguridad para proteger tu información
          personal. No vendemos, intercambiamos ni transferimos tu información
          personal a terceros sin tu consentimiento, excepto cuando sea
          necesario para completar una transacción.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-110">
          4. Cookies
        </h3>
        <p data-id="element-111">
          Este sitio puede utilizar cookies para mejorar la experiencia del
          usuario. Puedes configurar tu navegador para rechazar cookies, aunque
          esto podría afectar la funcionalidad del sitio.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-112">
          5. Tus Derechos
        </h3>
        <p data-id="element-113">
          Tienes derecho a acceder, rectificar o eliminar tu información
          personal en cualquier momento. Para ejercer estos derechos, puedes
          comunicarte con nosotros.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-114">
          6. Cambios a esta Política
        </h3>
        <p data-id="element-115">
          Nos reservamos el derecho de actualizar esta política en cualquier
          momento. Los cambios serán publicados en esta página con la fecha de
          actualización correspondiente.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-116">
        <p className="text-[#d4a853] font-semibold" data-id="element-117">
          Última actualización: Marzo 2026
        </p>
        <p data-id="element-118">
          Al acceder y utilizar este sitio web y/o adquirir el eBook "Activa tu
          intuición y transforma tu vida con las 7 Leyes Universales", aceptas
          los siguientes términos y condiciones.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-119">
          1. Producto Digital
        </h3>
        <p data-id="element-120">
          Este es un producto digital (eBook) que se entrega en formato
          electrónico. Al completar tu compra, recibirás acceso al material
          digital. Debido a la naturaleza del producto digital, todas las ventas
          son finales.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-121">
          2. Propiedad Intelectual
        </h3>
        <p data-id="element-122">
          Todo el contenido del eBook, incluyendo texto, ejercicios,
          metodologías y materiales bonus, es propiedad intelectual de Gustavo
          Rodríguez. Queda prohibida la reproducción, distribución o reventa
          total o parcial sin autorización expresa por escrito.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-123">
          3. Uso Personal
        </h3>
        <p data-id="element-124">
          La licencia de compra es para uso personal e individual. No está
          permitido compartir, copiar o distribuir el material a terceros.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-125">
          4. Descargo de Responsabilidad
        </h3>
        <p data-id="element-126">
          El contenido de este eBook es de carácter educativo e informativo. No
          sustituye asesoramiento profesional médico, psicológico o financiero.
          Los resultados pueden variar según la dedicación y aplicación
          individual de cada persona.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-127">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-128">
          Gustavo Rodríguez no se hace responsable de los resultados
          individuales obtenidos por la aplicación del contenido. El uso del
          material es bajo tu propia responsabilidad.
        </p>

        <h3 className="font-playfair text-lg text-[#f0c75e] mt-6" data-id="element-129">
          6. Ley Aplicable
        </h3>
        <p data-id="element-130">
          Estos términos se rigen por las leyes de los Estados Unidos de
          América. Cualquier disputa será resuelta bajo la jurisdicción
          correspondiente.
        </p>
      </LegalModal>
    </section>;
}